import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ExtractIcon from '@mui/icons-material/Transform';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { requestExtraction } from '../../hooks/scripts';
import { useError } from '@src/contexts/ErrorContext';
import { ScriptsDTO } from '@src/services/tools/modules/v1/scripts/dto/brain-scripts.getById.response.dto';

interface ExportOptionsModalProps {
  open: boolean;
  onClose: () => void;
  script: ScriptsDTO;
  onDownload: (format: 'csv' | 'xlsx', resolveResponse: boolean) => Promise<void>;
  onViewReports: () => void;
  onExtractRequested?: () => void;
}

export const ExportOptionsModal = ({
  open,
  onClose,
  script,
  onDownload,
  onViewReports,
  onExtractRequested,
}: ExportOptionsModalProps) => {
  const [isExtracting, setIsExtracting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { setError, setSuccess } = useError();

  const handleExtract = async () => {
    setIsExtracting(true);
    try {
      await requestExtraction(script.id);
      setSuccess({
        title: 'Extração solicitada',
        message: 'Sua solicitação de extração foi enviada e está sendo processada',
      });
      onClose();
      // Notify parent that extraction was requested
      if (onExtractRequested) {
        onExtractRequested();
      }
      // Auto redirect to reports view
      setTimeout(() => {
        onViewReports();
      }, 500);
    } catch (error: any) {
      console.error('Error requesting extraction:', error);
      setError({
        title: 'Erro ao solicitar extração',
        message: error?.response?.data?.message || error?.message || 'Não foi possível solicitar a extração',
        statusCode: error?.response?.status,
      });
    } finally {
      setIsExtracting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1e1e1e',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle sx={{ bgcolor: '#1e1e1e', borderBottom: '1px solid #333' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ color: '#fff' }}>
            Opções de Exportação
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: '#1e1e1e', p: 0 }}>
        <List sx={{ py: 0 }}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleExtract}
              disabled={isExtracting || isDownloading}
              sx={{
                py: 2,
                '&:hover': {
                  bgcolor: '#252525',
                },
              }}
            >
              <ListItemIcon sx={{ color: '#2196f3', minWidth: 40 }}>
                {isExtracting ? <CircularProgress size={24} sx={{ color: '#2196f3' }} /> : <ExtractIcon />}
              </ListItemIcon>
              <ListItemText
                primary="Solicitar Extração"
                secondary="Gerar novo relatório de extração"
                primaryTypographyProps={{ sx: { color: '#fff', fontWeight: 500 } }}
                secondaryTypographyProps={{ sx: { color: '#aaa' } }}
              />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                onClose();
                onViewReports();
              }}
              disabled={isExtracting || isDownloading}
              sx={{
                py: 2,
                '&:hover': {
                  bgcolor: '#252525',
                },
              }}
            >
              <ListItemIcon sx={{ color: '#2196f3', minWidth: 40 }}>
                <ListAltIcon />
              </ListItemIcon>
              <ListItemText
                primary="Ver Extrações"
                secondary="Visualizar relatórios gerados"
                primaryTypographyProps={{ sx: { color: '#fff', fontWeight: 500 } }}
                secondaryTypographyProps={{ sx: { color: '#aaa' } }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </DialogContent>

      <DialogActions sx={{ bgcolor: '#1e1e1e', borderTop: '1px solid #333', px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          disabled={isExtracting || isDownloading}
          sx={{
            color: '#aaa',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.08)',
            },
          }}
        >
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
